import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "trips-container"
}
const _hoisted_2 = { class: "trips-section" }
const _hoisted_3 = { class: "clickable-text" }
const _hoisted_4 = {
  key: 1,
  class: "no-trips"
}
const _hoisted_5 = { class: "trips-section" }
const _hoisted_6 = { class: "clickable-text" }
const _hoisted_7 = {
  key: 1,
  class: "no-trips"
}
const _hoisted_8 = {
  key: 0,
  class: "pagination-container"
}

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { DateTime } from 'luxon'

import { EvCarProfile } from '@/models/evCarProfile'

import * as evTripsApi from '@/api/ev-trips'
import { parseErrors } from '@/helpers/error-parsers'
import { commonCase } from '@/helpers/strings'

import Loader from '@/components/Loader.vue'
import CommonError from '@/components/CommonError.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'ReserveSection',
  props: {
    vehicle: {}
  },
  setup(__props: any) {

const props = __props

const router = useRouter()

const reservations = ref<any[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)

// Pagination state
const currentPage = ref(1)
const perPage = ref(20)
const totalReservations = ref(0)
const totalPages = ref(0)

// Computed properties for filtering reservations
const activeReservations = computed(() => {
  const active = reservations.value.filter(reservation =>
    ['reserved'].includes(reservation.status)
  )

  return active
})

const completedReservations = computed(() => {
  return reservations.value.filter(reservation =>
    ['finished'].includes(reservation.status)
  )
})

// Helper functions
const getRenterName = (reservation: any): string => {
  if (reservation.user) {
    return `${reservation.user.first_name || ''} ${reservation.user.last_name ||
      ''}`.trim()
  }
  return 'Unknown'
}

const getVehicleInfo = (reservation: any): string => {
  if (reservation.carProfile) {
    return reservation.carProfile.model_name || 'Unknown'
  }
  return 'Unknown'
}

const getPlateNumber = (reservation: any): string => {
  return reservation.carProfile?.plate_number || 'Unknown'
}

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-'
  try {
    return DateTime.fromISO(dateString).toLocaleString({
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Invalid date'
  }
}

const getStatusType = (status: string): string => {
  const statusMap: Record<string, string> = {
    active: 'success',
    confirmed: 'primary',
    pending: 'warning',
    reserved: 'info',
    completed: 'info',
    finished: 'success',
    cancelled: 'danger',
    expired: 'danger',
  }
  return statusMap[status] || 'info'
}

const getPaymentStatusType = (status: string): string => {
  const statusMap: Record<string, string> = {
    paid: 'success',
    unpaid: 'warning',
    refunded: 'danger',
  }
  return statusMap[status] || 'info'
}

const formatAmount = (amount: number | string | null): string => {
  if (amount === null || amount === undefined) return '-'

  // Convert string to number if needed
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount

  if (isNaN(numAmount)) return '-'

  return `₼${numAmount.toFixed(2)}`
}

const handleReservationClick = (reservation: any) => {
  // Navigate to user details when clicking on the row
  if (reservation.user?.id) {
    router.push(`/users/${reservation.user.id}`)
  }
}

const handlePageChange = async (page: number) => {
  currentPage.value = page
  await getVehicleReservations(page)
}

const handlePerPageChange = async (size: number) => {
  perPage.value = size
  currentPage.value = 1
  await getVehicleReservations(1)
}

const getVehicleReservations = async (page: number = currentPage.value) => {
  try {
    if (!props.vehicle?.id) {
      console.error('❌ Vehicle is not loaded yet')
      return
    }

    isLoading.value = true
    error.value = null

    const response = await evTripsApi.getReservationsByEVCar(props.vehicle.id, {
      page,
      perPage: perPage.value,
      sortBy: 'created_at',
      sortDirection: 'desc',
    })

    const apiReservations = response.data.data
    const meta = response.data.meta as any

    reservations.value = apiReservations || []
    currentPage.value = meta?.currentPage ?? meta?.current_page ?? page
    totalReservations.value = meta?.total ?? 0
    totalPages.value =
      meta?.lastPage ??
      meta?.last_page ??
      Math.ceil(totalReservations.value / perPage.value)
  } catch (e) {
    console.error('ERROR getting reservations:', e)
    error.value = parseErrors(e).error
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getVehicleReservations()
})

const filteredReservations = computed(() => {
  return reservations.value.filter(reservation =>
    ['finished', 'reserved'].includes(reservation.status)
  )
})

// Watch for vehicle changes and load reservations
watch(
  () => props.vehicle,
  newVehicle => {
    if (newVehicle?.id) {
      getVehicleReservations()
    }
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!

  return (error.value)
    ? (_openBlock(), _createBlock(CommonError, {
        key: 0,
        errorMessage: error.value,
        onRetryPress: getVehicleReservations
      }, null, 8, ["errorMessage"]))
    : (isLoading.value)
      ? (_openBlock(), _createBlock(Loader, { key: 1 }))
      : (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _cache[3] || (_cache[3] = _createElementVNode("h4", { class: "section-title" }, "ACTIVE RESERVATIONS", -1)),
            (activeReservations.value.length > 0)
              ? (_openBlock(), _createBlock(_component_el_table, {
                  key: 0,
                  data: activeReservations.value,
                  style: {"width":"100%"},
                  class: "trips-table",
                  onRowClick: handleReservationClick
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_table_column, {
                      label: "Renter",
                      "min-width": "180"
                    }, {
                      default: _withCtx((scope) => [
                        _createElementVNode("span", _hoisted_3, _toDisplayString(getRenterName(scope.row)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Start date",
                      "min-width": "160"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatDateTime(scope.row.start_time)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "End date",
                      "min-width": "160"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatDateTime(scope.row.end_time)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Make and Model",
                      "min-width": "150"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(getVehicleInfo(scope.row)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Plate number",
                      "min-width": "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(getPlateNumber(scope.row)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Status",
                      "min-width": "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_tag, {
                          type: getStatusType(scope.row.status)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(commonCase)(scope.row.status)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Payment Status",
                      "min-width": "140"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_tag, {
                          type: getPaymentStatusType(scope.row.status_paid)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(commonCase)(scope.row.status_paid)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Amount",
                      "min-width": "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatAmount(scope.row.amount)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["data"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_4, _cache[2] || (_cache[2] = [
                  _createElementVNode("p", null, "No active reservations found", -1)
                ])))
          ]),
          _createElementVNode("div", _hoisted_5, [
            _cache[5] || (_cache[5] = _createElementVNode("h4", { class: "section-title" }, "COMPLETED RESERVATIONS", -1)),
            (completedReservations.value.length > 0)
              ? (_openBlock(), _createBlock(_component_el_table, {
                  key: 0,
                  data: completedReservations.value,
                  style: {"width":"100%"},
                  class: "trips-table",
                  onRowClick: handleReservationClick
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_el_table_column, {
                      label: "Renter",
                      "min-width": "180"
                    }, {
                      default: _withCtx((scope) => [
                        _createElementVNode("span", _hoisted_6, _toDisplayString(getRenterName(scope.row)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Start date",
                      "min-width": "160"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatDateTime(scope.row.start_time)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "End date",
                      "min-width": "160"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatDateTime(scope.row.end_time)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Make and Model",
                      "min-width": "150"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(getVehicleInfo(scope.row)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Plate number",
                      "min-width": "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(getPlateNumber(scope.row)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Status",
                      "min-width": "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_tag, {
                          type: getStatusType(scope.row.status)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(commonCase)(scope.row.status)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Payment Status",
                      "min-width": "140"
                    }, {
                      default: _withCtx((scope) => [
                        _createVNode(_component_el_tag, {
                          type: getPaymentStatusType(scope.row.status_paid)
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(commonCase)(scope.row.status_paid)), 1)
                          ]),
                          _: 2
                        }, 1032, ["type"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_el_table_column, {
                      label: "Amount",
                      "min-width": "120"
                    }, {
                      default: _withCtx((scope) => [
                        _createTextVNode(_toDisplayString(formatAmount(scope.row.amount)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["data"]))
              : (_openBlock(), _createElementBlock("div", _hoisted_7, _cache[4] || (_cache[4] = [
                  _createElementVNode("p", null, "No completed reservations found", -1)
                ])))
          ]),
          (filteredReservations.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createVNode(_component_el_pagination, {
                  "current-page": currentPage.value,
                  "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
                  "page-size": perPage.value,
                  "onUpdate:pageSize": _cache[1] || (_cache[1] = ($event: any) => ((perPage).value = $event)),
                  total: totalReservations.value,
                  layout: "total, sizes, prev, pager, next",
                  "page-sizes": [10, 20, 50, 100],
                  "hide-on-single-page": false,
                  onCurrentChange: handlePageChange,
                  onSizeChange: handlePerPageChange,
                  background: ""
                }, null, 8, ["current-page", "page-size", "total"])
              ]))
            : _createCommentVNode("", true)
        ]))
}
}

})